/* eslint-disable */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Check, XCircle, RefreshCw } from "lucide-react";
import PricingSection from "@/components/PricingSection";

const LABELS = { free: "Free", basic: "Basic", pro: "Pro", business: "Business", firma: "Business" };

export default function Billing() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const desiredPlan = sp.get("plan");
  const desiredBilling = sp.get("billing") || "monthly";

  useEffect(() => {
    api.get("/plans").then((r) => setData(r.data)).catch(() => toast.error(t("billing.loadError")));
    refresh();
  }, []);

  const subscribe = async (plan_id, billing = "monthly") => {
    setBusyId(plan_id);
    try {
      const origin_url = window.location.origin;
      const r = await api.post("/billing/checkout", { plan_id, origin_url, billing });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("billing.checkoutErr"));
      setBusyId(null);
    }
  };

  // Auto-trigger checkout if landed here with ?plan=X (from landing or login)
  useEffect(() => {
    if (!desiredPlan || !data || !user) return;
    const p = data.plans?.[desiredPlan];
    if (!p || !p.stripe_enabled) { setSp({}, { replace: true }); return; }
    if (user.plan === desiredPlan && user.subscription_status === "active") {
      toast.info(t("billing.alreadySubscribed"));
      setSp({}, { replace: true });
      return;
    }
    setSp({}, { replace: true });
    subscribe(desiredPlan, desiredBilling);
    // eslint-disable-next-line
  }, [desiredPlan, data, user?.id]);

  const cancelSub = async () => {
    if (!window.confirm(t("billing.cancelConfirm"))) return;
    setCancelBusy(true);
    try {
      const r = await api.post("/billing/cancel");
      toast.success(r.data.message);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("billing.error"));
    } finally {
      setCancelBusy(false);
    }
  };

  const reactivate = async () => {
    setCancelBusy(true);
    try {
      const r = await api.post("/billing/reactivate");
      toast.success(r.data.message);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("billing.error"));
    } finally {
      setCancelBusy(false);
    }
  };

  if (!data) return <div className="p-10 text-sm text-[#94A3B8]">{t("billing.loading")}</div>;

  const isAdminUnlimited = user?.subscription_status === "admin";
  const currentPlan = user?.plan === "firma" ? "business" : user?.plan;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <div className="text-center max-w-2xl mx-auto">
        <div className="overline">{t("billing.overline")}</div>
        <h1 className="font-display text-3xl lg:text-5xl font-extrabold tracking-tight mt-2">{t("billing.title")}</h1>
        <p className="mt-4 text-[#94A3B8]">{t("billing.subtitle")}</p>
        <p className="text-sm text-[#94A3B8] mt-3">{t("billing.currentPlan")}: <b className="text-[#E2E8F0]">{LABELS[user?.plan] || user?.plan}</b> · {t("billing.status")}: <b className="text-[#E2E8F0]">{user?.subscription_status || "none"}</b></p>
      </div>

      {/* Current subscription panel */}
      {user && user.plan !== "free" && !isAdminUnlimited && (
        <div className="card-flat p-6 mt-8 max-w-3xl mx-auto" data-testid="subscription-status-card">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="overline">{t("billing.yourSubscription")}</div>
              <h3 className="font-display text-xl font-bold mt-2">{LABELS[user.plan]} — {user.subscription_status === "cancelled" ? t("billing.stateCancelled") : t("billing.stateActive")}</h3>
              {user.subscription_end && (
                <p className="text-sm text-[#94A3B8] mt-1">
                  {user.subscription_status === "cancelled" ? t("billing.expiresOn", { date: user.subscription_end.slice(0, 10) }) : t("billing.renewsOn", { date: user.subscription_end.slice(0, 10) })}
                </p>
              )}
            </div>
            {user.subscription_status === "cancelled" ? (
              <button onClick={reactivate} disabled={cancelBusy} className="btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="reactivate-btn">
                <RefreshCw size={16} /> {t("billing.reactivate")}
              </button>
            ) : (
              <button onClick={cancelSub} disabled={cancelBusy} className="btn-outline flex items-center gap-2 disabled:opacity-50 text-[#EF4444] border-[#EF4444] hover:bg-[#2A1416]" data-testid="cancel-subscription-btn">
                <XCircle size={16} /> {t("billing.cancelSub")}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-12">
        <PricingSection
          plans={data.plans}
          addons={data.addons}
          user={user}
          currentPlan={currentPlan}
          busyId={busyId}
          onSelectPlan={subscribe}
          onSelectAddon={subscribe}
        />
      </div>
    </div>
  );
}
